import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
// import UserProfile from './components/UserProfile'; Commenting to resolve build error
import AppHeader from './components/AppHeader';
import SimpleHeader from './components/SimpleHeader';
import RechargeModal from './components/RechargeModal';
import LoginPage from './components/LoginPage';
import TermsPage from './components/TermsPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import RefundPolicyPage from './components/RefundPolicyPage';
import ContactPage from './components/ContactPage';
import AboutUsPage from './components/AboutUsPage';
import PricingPage from './components/PricingPage';
import PaymentStatusPage from './components/PaymentStatusPage';
import PhonePeTestPage from './components/PhonePeTestPage';
import MergePage from './components/MergePage';
import GeneratedReportPage from './components/GeneratedReportPage';
import AdminPanel from './components/AdminPanel';
import Footer from './components/Footer';
import CTABanner from './components/CTABanner';
import FAQ from './components/FAQ';
import PricingSection from './components/PricingSection';
import FeaturesSection from './components/FeaturesSection';
import './App.css';
import './App.minimal.css';

// Constants
const DEFAULT_GENERATION_COST = 2;

// Aesthetic background options for jewelry photography
const aestheticBackgroundOptions = [
  'luxurious deep emerald velvet cloth with rich texture and soft folds',
  'premium burgundy wine velvet backdrop with subtle depth gradation',
  'elegant midnight navy velvet surface with professional studio lighting',
  'solid charcoal gray background, dark gray color, neutral charcoal tone, no purple or blue tints, pure charcoal gray backdrop',
  'pristine white Italian silk with soft directional lighting and minimal texture',
  'warm champagne silk backdrop with subtle golden undertones and gentle draping'
];

// Background color mapping for aesthetic style
const backgroundColorMap = {
  'random': null, // Will use randomChoice
  'emerald': 'luxurious deep emerald velvet cloth with rich texture and soft folds',
  'burgundy': 'premium burgundy wine velvet backdrop with subtle depth gradation',
  'navy': 'elegant midnight navy velvet surface with professional studio lighting',
  'charcoal': 'solid charcoal gray background, dark gray color, neutral charcoal tone, no purple or blue tints, pure charcoal gray backdrop',
  'white': 'pristine white Italian silk with soft directional lighting and minimal texture',
  'champagne': 'warm champagne silk backdrop with subtle golden undertones and gentle draping'
};

// Helper function to randomly select from an array
// Logic:
// 1. Math.random() generates a random number between 0 (inclusive) and 1 (exclusive)
// 2. Multiply by arr.length to get a number between 0 and arr.length (exclusive)
// 3. Math.floor() rounds down to get an integer index between 0 and arr.length-1
// Example: For 6 items, Math.random() * 6 gives 0.0 to 5.999..., floor gives 0-5
const randomChoice = (arr) => {
  if (!arr || arr.length === 0) {
    return null;
  }
  // Generate a random index
  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
};

// Backend API URL - configurable per environment
// Preferred: REACT_APP_PUBLIC_API_URL
// Fallback: production URL for prod builds, localhost for dev
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

// Main App component for PhotoShot AI
function App() {
  // Authentication context
  const { user, loading, isAuthenticated, logout } = useAuth();
  
  // State management for the application
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [jewelryCategory, setJewelryCategory] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [prompt, setPrompt] = useState('Transform this jewelry into a professional model photoshoot. Show a beautiful Indian model wearing this jewelry piece elegantly. Focus on the jewelry details, make it the main subject. Professional lighting, clean background, high-quality fashion photography style.');
  const [style, setStyle] = useState('realistic');
  // When style is 'aesthetic', it means abstract background (jewelry only)
  const abstractBackground = style === 'aesthetic';
  const [aspectRatio, setAspectRatio] = useState('1:1'); // Size options: 1:1, 2:3, 1:3
  const [angle, setAngle] = useState('front'); // Angle options
  const [modelGender, setModelGender] = useState('female'); // Model gender: 'female' or 'male', default 'female'
  const [aestheticBackgroundColor, setAestheticBackgroundColor] = useState('random'); // Background color for aesthetic style: 'random', 'emerald', 'burgundy', 'navy', 'charcoal', 'white', 'champagne'
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [generationError, setGenerationError] = useState(null); // Error state for image generation
  const [generationCost, setGenerationCost] = useState(DEFAULT_GENERATION_COST);
  
  // Ref for file input
  const fileInputRef = useRef(null);
  
  // Wallet and user stats state
  const [walletInfo, setWalletInfo] = useState({ credits: 0, totalSpent: 0 });
  const [userStats, setUserStats] = useState({ imagesGenerated: 0 });
  const [showRechargeModal, setShowRechargeModal] = useState(false);

  // Track to prevent duplicate API calls on mount (e.g., React StrictMode)
  const hasFetchedUserDataRef = useRef(false);
  const hasFetchedCostsRef = useRef(false);

  // Fetch wallet and profile information exactly once per session
  const fetchUserData = useCallback(async () => {
    if (hasFetchedUserDataRef.current) return;
    hasFetchedUserDataRef.current = true;
    try {
      // Use token from AuthContext (or fallback to localStorage with correct key)
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Axios defaults are already set in AuthContext, but explicitly set header to be safe
      const [walletResponse, profileResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/wallet`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Update wallet info if response is successful
      if (walletResponse.data.success) {
        setWalletInfo(walletResponse.data.data);
      } else {
        console.error('❌ Wallet fetch failed:', walletResponse.data.error);
      }

      // Update user stats if response is successful
      if (profileResponse.data.success) {
        setUserStats({
          imagesGenerated: profileResponse.data.data.imagesGenerated
        });
      } else {
        console.error('❌ Profile fetch failed:', profileResponse.data.error);
      }
    } catch (error) {
      console.error('❌ Error fetching user data:', error);
      // Log detailed error for debugging
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
    }
  }, []);

  // Function to check and process pending payments when user returns
  // This handles the case where user closed browser after payment
  const checkPendingPayments = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Check most recent pending payment status (without transaction ID)
      // Backend will automatically check the most recent pending payment
      const response = await axios.get(
        `${BACKEND_URL}/api/payment/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        const paymentStatus = response.data.data?.status || response.data.data?.ourTransaction?.status;
        if (paymentStatus === 'success') {
          // Payment was successful, refresh user data to show updated credits
        //  console.log('✅ Found successful pending payment, refreshing credits');
          hasFetchedUserDataRef.current = false; // Reset flag to allow refresh
          fetchUserData();
        }
      }
    } catch (error) {
      // Silently fail - this is a background check, don't show errors to user
      // 404 is expected if no pending payments exist
      if (error.response?.status !== 404) {
        console.log('ℹ️ No pending payments or error checking:', error.message);
      }
    }
  }, [fetchUserData]);

  // Fetch user data when component mounts or when auth state changes
  React.useEffect(() => {
    if (isAuthenticated()) {
      fetchUserData();
      // Check for pending payments when user returns (handles browser close scenario)
      // Delay by 1 second to let user data load first
      setTimeout(() => {
        checkPendingPayments();
      }, 1000);
    }
  }, [user, isAuthenticated, checkPendingPayments]); // Re-run when user or isAuthenticated changes

  // Listen for payment success event to refresh user data
  React.useEffect(() => {
    const handleRefreshUserData = () => {
      if (isAuthenticated()) {
        hasFetchedUserDataRef.current = false; // Reset flag to allow refresh
        fetchUserData();
      }
    };

    window.addEventListener('refreshUserData', handleRefreshUserData);
    return () => {
      window.removeEventListener('refreshUserData', handleRefreshUserData);
    };
  }, [isAuthenticated, fetchUserData]);

  // Load effective per-user generation cost from backend (once)
  useEffect(() => {
    if (hasFetchedCostsRef.current) return;
    hasFetchedCostsRef.current = true;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const endpoint = token ? `${BACKEND_URL}/api/costs/effective` : `${BACKEND_URL}/api/costs`;
        const { data } = await axios.get(endpoint, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
        if (data?.success) {
          setGenerationCost(data.data.generationCost ?? DEFAULT_GENERATION_COST);
        }
      } catch (_) {}
    })();
  }, []);

  // Generate unique prompt variations based on jewelry category
  // useAbstractBg is derived from style === 'aesthetic'
  const generatePromptByCategory = (category, selectedStyle = null, selectedAngle = null, selectedGender = 'female', selectedBgColor = 'random') => {
    const useAbstractBg = selectedStyle === 'aesthetic';
    // Helper function to randomly select from an array
    // Note: randomChoice is now a top-level constant
    // Define prompt components for variety based on gender
    const modelDescriptions = selectedGender === 'male' ? [
      'handsome Indian model',
      'elegant Indian man',
      'stunning Indian male model',
      'graceful Indian gentleman',
      'radiant Indian male model'
    ] : [
      'beautiful Indian model',
      'elegant Indian woman',
      'stunning Indian model',
      'graceful Indian model',
      'radiant Indian woman'
    ];
    
    const lightingOptions = [
      'Professional studio lighting',
      'Soft natural lighting',
      'Dramatic fashion lighting',
      'Elegant ambient lighting',
      'Studio lighting with soft shadows'
    ];
    
    // Background options - include abstract backgrounds if selected
    // Note: aestheticBackgroundOptions is now a top-level constant
    // For aesthetic style, use selected color or random
    const getAestheticBackground = () => {
      if (selectedBgColor === 'random' || !backgroundColorMap[selectedBgColor]) {
        return randomChoice(aestheticBackgroundOptions);
      }
      return backgroundColorMap[selectedBgColor];
    };
    
    const backgroundOptions = useAbstractBg ? aestheticBackgroundOptions : [
      'clean white background',
      'minimalist neutral background',
      'soft gradient background',
      'elegant monochrome background',
      'studio backdrop background'
    ];
    
    const styleOptions = [
      'traditional Indian fashion photography style',
      'modern Indian fashion photography style',
      'editorial Indian fashion photography style',
      'luxury Indian fashion photography style',
      'high-end Indian fashion photography style'
    ];
    
    // Angle mapping from user selection to prompt description
    const angleMap = {
      'front': 'close-up shot from front angle',
      'side': 'close-up portrait from slight side angle',
      '45-degree': 'close-up view from 45-degree angle',
      'three-quarter': 'close-up shot from three-quarter angle',
      'diagonal': 'close-up perspective from diagonal angle',
      'profile': 'close-up view from profile angle',
      'upward': 'close-up shot from upward angle',
      'downward': 'close-up portrait from downward angle',
      'eye-level': 'close-up view from eye-level angle'
    };
    
    // Use selected angle or random from available options
    const angleOptions = Object.values(angleMap);
    const selectedAngleDesc = selectedAngle && angleMap[selectedAngle] ? angleMap[selectedAngle] : null;
    
    // Close pose emphasis variations
    const closePoseEmphasis = [
      'close-up pose',
      'intimate close-up pose',
      'dramatic close-up pose',
      'elegant close-up pose',
      'professional close-up pose'
    ];
    
    // Category-specific prompt variations
    // If abstractBackground is true, use jewelry-only prompts (no model) with aesthetic backgrounds
    // Get the selected background color description - use this instead of randomChoice
    const getSelectedBackground = () => {
      if (!useAbstractBg) return null;
      return getAestheticBackground(selectedBgColor);
    };
    const selectedBgDescription = getSelectedBackground();
    const categoryPrompts = useAbstractBg ? {
      'Necklaces / Necklace Sets': [
        (l, a) => `High-end product photography of this delicate necklace elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The necklace should be the main focal point, gracefully arranged and highlighting its intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite necklace elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the necklace itself, no human model. Focus on showcasing its elegant design and craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful necklace elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The necklace is elegantly arranged to showcase its detailed design and intricate patterns. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Earrings': [
        (l, a) => `High-end product photography of these elegant earrings elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry pieces, no model or person. The earrings should be the main focal point, elegantly arranged and highlighting their intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of these exquisite earrings elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the earrings themselves, no human model. Focus on showcasing their elegant design and craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of these beautiful earrings elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry pieces without any model. The pieces are elegantly arranged to showcase their detailed design and brilliant shine. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Rings': [
        (l, a) => `High-end product photography of this exquisite ring elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The ring should be the main focal point, elegantly positioned and highlighting its design and craftsmanship. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this beautiful ring elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the ring itself, no human model. Focus on showcasing its intricate design and elegant setting. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this stunning ring elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The ring is prominently displayed, highlighting its beauty and craftsmanship. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Bracelets': [
        (l, a) => `High-end product photography of this elegant bracelet elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The bracelet should be the main focal point, elegantly arranged and showcasing its intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite bracelet elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the bracelet itself, no human model. Focus on showcasing its elegant design and traditional craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful bracelet elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The bracelet is elegantly arranged to showcase its intricate details. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Bangles': [
        (l, a) => `High-end product photography of these elegant bangles elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry pieces, no model or person. The bangles should be the main focal point, elegantly stacked and arranged, highlighting their traditional design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of these exquisite bangles elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the bangles themselves, no human model. Focus on showcasing their elegant design and traditional craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of these beautiful bangles elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry pieces without any model. The bangles are elegantly stacked to showcase their shine and traditional design. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Pendants': [
        (l, a) => `High-end product photography of this elegant pendant elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The pendant should be the main focal point, elegantly arranged and highlighting its intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite pendant elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the pendant itself, no human model. Focus on showcasing its elegant design and traditional craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful pendant elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The pendant is elegantly arranged to showcase its detailed design. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Chains': [
        (l, a) => `High-end product photography of this elegant chain elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The chain should be the main focal point, elegantly arranged and showcasing its links and design details. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite chain elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the chain itself, no human model. Focus on showcasing its intricate links and contemporary design. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful chain elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The chain is elegantly displayed, showcasing its design and craftsmanship. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Anklets / Payal': [
        (l, a) => `High-end product photography of this elegant anklet elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The anklet should be the main focal point, elegantly arranged and highlighting traditional Indian jewelry design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite anklet elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the anklet itself, no human model. Focus on showcasing its traditional design and cultural significance. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful anklet elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The anklet is elegantly arranged to showcase its traditional craftsmanship. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Brooches': [
        (l, a) => `High-end product photography of this elegant brooch elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The brooch should be the main focal point, elegantly positioned and highlighting its intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite brooch elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the brooch itself, no human model. Focus on showcasing its elegant design and traditional craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful brooch elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The brooch is elegantly arranged to showcase its detailed design. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Nose Rings / Nose Pins': [
        (l, a) => `High-end product photography of this elegant nose ring elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The nose ring should be the main focal point, elegantly positioned and highlighting traditional Indian jewelry design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite nose ring elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the nose ring itself, no human model. Focus on showcasing its traditional design and cultural significance. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful nose ring elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The nose ring is elegantly arranged to showcase its traditional craftsmanship. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Mangalsutra': [
        (l, a) => `High-end product photography of this elegant mangalsutra elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The mangalsutra should be the main focal point, elegantly arranged and highlighting its sacred design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite mangalsutra elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the mangalsutra itself, no human model. Focus on showcasing its sacred design and cultural significance. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful mangalsutra elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The traditional piece is elegantly arranged to showcase its sacred design. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Waist Belts / Vaddanam / Kamarbandh': [
        (l, a) => `High-end product photography of this elegant waist belt elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The belt should be the main focal point, elegantly arranged and highlighting its intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite kamarbandh elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the waist belt itself, no human model. Focus on showcasing its traditional Indian waist jewelry design and craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful kamarbandh elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The belt is elegantly arranged to showcase its intricate design. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Maang Tikka / Head Jewelry': [
        (l, a) => `High-end product photography of this elegant maang tikka elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The head jewelry should be the main focal point, elegantly positioned and highlighting traditional Indian bridal design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite maang tikka elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the head jewelry itself, no human model. Focus on showcasing its traditional Indian bridal design and cultural elegance. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful maang tikka elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The head jewelry is elegantly arranged to showcase its traditional bridal significance. ${l}, captured from ${a}, premium product photography style.`
      ],
      'Hair Accessories': [
        (l, a) => `High-end product photography of this elegant hair accessory elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece, no model or person. The accessory should be the main focal point, elegantly arranged and highlighting its intricate design. ${l}, captured from ${a}, professional product photography style.`,
        (l, a) => `High-end product photography of this exquisite hair accessory elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Display only the accessory itself, no human model. Focus on showcasing its elegant design and traditional craftsmanship. ${l}, captured from ${a}, sophisticated product photography aesthetic.`,
        (l, a) => `High-end product photography of this beautiful hair accessory elegantly displayed on ${selectedBgDescription || randomChoice(aestheticBackgroundOptions)}. Show only the jewelry piece without any model. The accessory is elegantly arranged to showcase its detailed design. ${l}, captured from ${a}, premium product photography style.`
      ]
    } : {
      // Original prompts with model (when abstractBackground is false)
      'Necklaces / Necklace Sets': [
        (m, l, b, s, a, c) => `Transform this necklace into a professional model photoshoot with ${c}. Show a ${m} wearing this necklace elegantly around her neck in a ${a}. The necklace should be the main focal point, gracefully draping and highlighting the intricate design. ${l}, ${b}, ${s} with modern aesthetic.`,
        (m, l, b, s, a, c) => `Create a stunning photoshoot featuring this necklace. A ${m} wears this exquisite necklace in a ${c}, ${a}, showcasing its elegant design and craftsmanship. ${l}, ${b}, ${s} emphasizing the jewelry's beauty.`,
        (m, l, b, s, a, c) => `Professional model photoshoot: ${m} wearing this beautiful necklace in ${c}, captured from ${a}. The piece gracefully frames the neckline, drawing attention to its detailed design. ${l}, ${b}, ${s} with sophisticated composition.`
      ],
      'Earrings': [
        (m, l, b, s, a, c) => `Transform this earring into a professional model photoshoot with ${c}. Show a ${m} wearing these earrings in her ears, elegantly framing her face, captured from ${a}. The earrings catch the light beautifully, showcasing intricate details. ${l}, ${b}, ${s} with attention to earring craftsmanship.`,
        (m, l, b, s, a, c) => `Elegant photoshoot featuring these earrings. A ${m} displays these stunning earrings in ${c}, ${a}, positioned perfectly to highlight their design. ${l}, ${b}, ${s} focusing on the earrings' elegance and detail.`,
        (m, l, b, s, a, c) => `Professional jewelry photoshoot: ${m} wearing these exquisite earrings in ${c}, shown from ${a}. The pieces elegantly frame her face, showcasing their beautiful design and shine. ${l}, ${b}, ${s} emphasizing traditional Indian jewelry beauty.`
      ],
      'Rings': [
        (m, l, b, s, a, c) => `Transform this ring into a professional model photoshoot with ${c}. Show a ${m} wearing this ring on her finger, gracefully posed with hands visible, captured from ${a}. The ring is the main focal point, highlighting its design and craftsmanship. ${l}, ${b}, elegant Indian fashion photography style.`,
        (m, l, b, s, a, c) => `Stunning ring photoshoot: ${m} elegantly displays this beautiful ring in ${c}, ${a}. Her hands are posed to showcase the ring's intricate design and elegant setting. ${l}, ${b}, ${s} with sophisticated styling.`,
        (m, l, b, s, a, c) => `Professional photoshoot featuring this ring. A ${m} wears this exquisite piece in ${c}, with the ring prominently displayed from ${a} against elegant hand poses. ${l}, ${b}, ${s} highlighting the ring's beauty and craftsmanship.`
      ],
      'Bracelets': [
        (m, l, b, s, a, c) => `Transform this bracelet into a professional model photoshoot with ${c}. Show a ${m} wearing this bracelet on her wrist, elegantly positioned, captured from ${a}. The bracelet is prominently displayed, showcasing its intricate design and beauty. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Elegant bracelet photoshoot: ${m} wearing this stunning piece in ${c}, ${a}. The bracelet gracefully adorns her wrist, highlighting its design and traditional craftsmanship. ${l}, ${b}, ${s} with refined composition.`,
        (m, l, b, s, a, c) => `Professional model photoshoot featuring this bracelet. A ${m} displays this beautiful bracelet in ${c}, positioned from ${a} to showcase its elegant design and intricate details. ${l}, ${b}, ${s} emphasizing traditional Indian jewelry.`
      ],
      'Bangles': [
        (m, l, b, s, a, c) => `Transform this bangle into a professional model photoshoot with ${c}. Show a ${m} wearing these bangles on her wrists, elegantly stacked and displayed, captured from ${a}. The bangles catch the light beautifully, showcasing their traditional design. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Beautiful bangle photoshoot: ${m} wearing these elegant bangles in ${c}, ${a}. Stacked gracefully on her wrists, they showcase traditional Indian jewelry design. ${l}, ${b}, ${s} with cultural elegance.`,
        (m, l, b, s, a, c) => `Professional photoshoot featuring these bangles. A ${m} displays these stunning bangles in ${c}, stacked on her wrists and captured from ${a}, highlighting their shine and traditional craftsmanship. ${l}, ${b}, ${s} with sophisticated styling.`
      ],
      'Pendants': [
        (m, l, b, s, a, c) => `Transform this pendant into a professional model photoshoot with ${c}. Show a ${m} wearing this pendant around her neck, gracefully suspended and highlighting the design, captured from ${a}. The pendant is the main focal point. ${l}, ${b}, traditional Indian jewelry photography style.`,
        (m, l, b, s, a, c) => `Elegant pendant photoshoot: ${m} wearing this beautiful pendant in ${c}, ${a}. Suspended gracefully around her neck, it highlights the intricate design and traditional craftsmanship. ${l}, ${b}, ${s} with refined aesthetics.`,
        (m, l, b, s, a, c) => `Professional model photoshoot featuring this pendant. A ${m} displays this exquisite pendant in ${c}, positioned from ${a} to showcase its elegant design and cultural significance. ${l}, ${b}, ${s} emphasizing beauty.`
      ],
      'Chains': [
        (m, l, b, s, a, c) => `Transform this chain into a professional model photoshoot with ${c}. Show a ${m} wearing this chain around her neck, elegantly draped, captured from ${a}. The chain showcases its links and design details beautifully. ${l}, ${b}, modern Indian fashion photography style.`,
        (m, l, b, s, a, c) => `Stunning chain photoshoot: ${m} wearing this elegant chain in ${c}, ${a}. Draped gracefully around her neck, it highlights the intricate links and contemporary design. ${l}, ${b}, ${s} with modern sophistication.`,
        (m, l, b, s, a, c) => `Professional model photoshoot: ${m} wearing this beautiful chain in ${c}, shown from ${a}. The piece elegantly frames her neckline, showcasing its design and craftsmanship. ${l}, ${b}, ${s} with refined composition.`
      ],
      'Anklets / Payal': [
        (m, l, b, s, a, c) => `Transform this anklet into a professional model photoshoot with ${c}. Show a ${m} wearing this anklet around her ankle, elegantly displayed, captured from ${a}. The anklet is the main focal point, highlighting traditional Indian jewelry design. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Beautiful anklet photoshoot: ${m} wearing this traditional payal in ${c}, ${a}. Gracefully adorning her ankle, it showcases traditional Indian jewelry design and cultural elegance. ${l}, ${b}, ${s} with cultural significance.`,
        (m, l, b, s, a, c) => `Professional photoshoot featuring this anklet. A ${m} displays this stunning payal in ${c}, positioned from ${a} to highlight its traditional design and intricate craftsmanship. ${l}, ${b}, ${s} emphasizing beauty.`
      ],
      'Brooches': [
        (m, l, b, s, a, c) => `Transform this brooch into a professional model photoshoot with ${c}. Show a ${m} wearing this brooch pinned on her outfit, elegantly positioned, captured from ${a}. The brooch is prominently displayed, catching the light beautifully. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Elegant brooch photoshoot: ${m} wearing this exquisite piece in ${c}, ${a}. Pinned gracefully on her outfit, it highlights the intricate design and traditional craftsmanship. ${l}, ${b}, ${s} with sophisticated styling.`,
        (m, l, b, s, a, c) => `Professional model photoshoot featuring this brooch. A ${m} displays this beautiful brooch in ${c}, positioned from ${a} to showcase its elegant design and cultural significance. ${l}, ${b}, ${s} with refined aesthetics.`
      ],
      'Nose Rings / Nose Pins': [
        (m, l, b, s, a, c) => `Transform this nose ring into a professional model photoshoot with ${c}. Show a ${m} wearing this nose ring, elegantly placed, captured from ${a}. The nose ring is prominently displayed, highlighting traditional Indian jewelry design. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Beautiful nose ring photoshoot: ${m} wearing this traditional piece in ${c}, ${a}. Positioned elegantly, it showcases traditional Indian jewelry design and cultural elegance. ${l}, ${b}, ${s} with cultural significance.`,
        (m, l, b, s, a, c) => `Professional photoshoot featuring this nose ring. A ${m} displays this stunning piece in ${c}, positioned from ${a} to highlight its traditional design and intricate craftsmanship. ${l}, ${b}, ${s} emphasizing beauty.`
      ],
      'Mangalsutra': [
        (m, l, b, s, a, c) => `Transform this mangalsutra into a professional model photoshoot with ${c}. Show a ${m} wearing this mangalsutra around her neck, elegantly draped as a traditional married woman, captured from ${a}. The mangalsutra is the main focal point, showcasing its traditional design. ${l}, ${b}, traditional Indian wedding jewelry photography style.`,
        (m, l, b, s, a, c) => `Elegant mangalsutra photoshoot: ${m} wearing this traditional piece in ${c}, ${a}. Draped gracefully around her neck, it highlights the sacred design and cultural significance of Indian wedding jewelry. ${l}, ${b}, ${s} with traditional elegance.`,
        (m, l, b, s, a, c) => `Professional wedding jewelry photoshoot: ${m} wearing this beautiful mangalsutra in ${c}, shown from ${a}. The traditional piece is elegantly displayed, showcasing its sacred design and cultural importance. ${l}, ${b}, ${s} with refined composition.`
      ],
      'Waist Belts / Vaddanam / Kamarbandh': [
        (m, l, b, s, a, c) => `Transform this waist belt into a professional model photoshoot with ${c}. Show a ${m} wearing this waist belt around her waist, elegantly cinched, captured from ${a}. The belt is prominently displayed, highlighting its intricate design. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Stunning kamarbandh photoshoot: ${m} wearing this elegant vaddanam in ${c}, ${a}. Gracefully cinched around her waist, it showcases traditional Indian waist jewelry design and craftsmanship. ${l}, ${b}, ${s} with cultural elegance.`,
        (m, l, b, s, a, c) => `Professional photoshoot featuring this waist belt. A ${m} displays this beautiful kamarbandh in ${c}, positioned from ${a} to highlight its intricate design and traditional Indian jewelry craftsmanship. ${l}, ${b}, ${s} emphasizing beauty.`
      ],
      'Maang Tikka / Head Jewelry': [
        (m, l, b, s, a, c) => `Transform this maang tikka into a professional model photoshoot with ${c}. Show a ${m} wearing this maang tikka on her forehead, elegantly positioned, captured from ${a}. The head jewelry is the main focal point, highlighting traditional Indian bridal design. ${l}, ${b}, traditional Indian wedding jewelry photography style.`,
        (m, l, b, s, a, c) => `Beautiful bridal head jewelry photoshoot: ${m} wearing this exquisite maang tikka in ${c}, ${a}. Positioned elegantly on her forehead, it showcases traditional Indian bridal design and cultural elegance. ${l}, ${b}, ${s} with wedding sophistication.`,
        (m, l, b, s, a, c) => `Professional bridal photoshoot featuring this maang tikka. A ${m} displays this stunning head jewelry in ${c}, positioned from ${a} to highlight its traditional design and bridal significance. ${l}, ${b}, ${s} emphasizing beauty.`
      ],
      'Hair Accessories': [
        (m, l, b, s, a, c) => `Transform this hair accessory into a professional model photoshoot with ${c}. Show a ${m} wearing this hair accessory, elegantly styled in her hair, captured from ${a}. The accessory is prominently displayed, catching the light beautifully. ${l}, ${b}, ${s}.`,
        (m, l, b, s, a, c) => `Elegant hair accessory photoshoot: ${m} wearing this beautiful piece in ${c}, ${a}. Styled gracefully in her hair, it highlights the intricate design and traditional craftsmanship. ${l}, ${b}, ${s} with sophisticated styling.`,
        (m, l, b, s, a, c) => `Professional model photoshoot featuring this hair accessory. A ${m} displays this exquisite piece in ${c}, positioned from ${a} to showcase its elegant design and cultural significance. ${l}, ${b}, ${s} with refined aesthetics.`
      ]
    };
    
    // Get prompts for the category
    const prompts = categoryPrompts[category];
    if (!prompts) {
      if (useAbstractBg) {
        const randomBg = randomChoice(aestheticBackgroundOptions);
        return `High-end product photography of this elegant jewelry piece elegantly displayed on ${randomBg}. Show only the jewelry piece, no model or person. The jewelry should be the main focal point, elegantly arranged and highlighting its intricate design. Professional studio lighting, captured from close-up shot from front angle, professional product photography style.`;
      }
      return 'Transform this jewelry into a professional model photoshoot with close-up pose. Show a beautiful Indian model wearing this jewelry piece elegantly in a close-up shot. Focus on the jewelry details, make it the main subject. Professional lighting, clean background, high-quality fashion photography style.';
    }
    
    // Generate a unique prompt each time with angle and close pose variations
    const template = randomChoice(prompts);
    const lighting = randomChoice(lightingOptions);
    const background = randomChoice(backgroundOptions);
    const style = randomChoice(styleOptions);
    const angle = selectedAngleDesc || randomChoice(angleOptions);
    
    // If abstract background is selected (style === 'aesthetic'), don't include model in the prompt
    if (useAbstractBg) {
      return template(lighting, angle);
    } else {
      const model = randomChoice(modelDescriptions);
      const closePose = randomChoice(closePoseEmphasis);
      // For non-aesthetic styles, use the selected style in the prompt
      const selectedStyleDesc = selectedStyle && selectedStyle !== 'aesthetic' ? selectedStyle : randomChoice(styleOptions);
      return template(model, lighting, background, selectedStyleDesc, angle, closePose);
    }
  };

  // Generate prompt for jewelry set with model (when style is not aesthetic)
  const generateJewelrySetModelPrompt = (selectedStyle, selectedAngle, selectedGender) => {
    const modelDescriptions = selectedGender === 'male' ? [
      'handsome Indian model',
      'elegant Indian man',
      'stunning Indian male model',
      'graceful Indian gentleman',
      'radiant Indian male model'
    ] : [
      'beautiful Indian model',
      'elegant Indian woman',
      'stunning Indian model',
      'graceful Indian model',
      'radiant Indian woman'
    ];
    
    const lightingOptions = [
      'Professional studio lighting',
      'Soft natural lighting',
      'Dramatic fashion lighting',
      'Elegant ambient lighting',
      'Studio lighting with soft shadows'
    ];
    
    const backgroundOptions = [
      'clean white background',
      'minimalist neutral background',
      'soft gradient background',
      'elegant monochrome background',
      'studio backdrop background'
    ];
    
    const styleOptions = [
      'traditional Indian fashion photography style',
      'modern Indian fashion photography style',
      'editorial Indian fashion photography style',
      'luxury Indian fashion photography style',
      'high-end Indian fashion photography style'
    ];
    
    // Angle mapping from user selection to prompt description
    const angleMap = {
      'front': 'close-up shot from front angle',
      'side': 'close-up portrait from slight side angle',
      '45-degree': 'close-up view from 45-degree angle',
      'three-quarter': 'close-up shot from three-quarter angle',
      'diagonal': 'close-up perspective from diagonal angle',
      'profile': 'close-up view from profile angle',
      'upward': 'close-up shot from upward angle',
      'downward': 'close-up portrait from downward angle',
      'eye-level': 'close-up view from eye-level angle'
    };
    
    const selectedAngleDesc = selectedAngle && angleMap[selectedAngle] ? angleMap[selectedAngle] : angleMap['front'];
    const model = randomChoice(modelDescriptions);
    const lighting = randomChoice(lightingOptions);
    const background = randomChoice(backgroundOptions);
    const styleDesc = selectedStyle && selectedStyle !== 'aesthetic' ? selectedStyle : randomChoice(styleOptions);
    
    // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
    return `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Place ONLY the jewelry pieces from the uploaded image onto a ${model}. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. If the image shows a pearl necklace and pearl earrings, show ONLY those - do not add a maang tikka or any other pieces. Place each piece on the model in the appropriate location (necklace on neck, earrings on ears, etc.). 

The jewelry in the final image must be identical to what's in the uploaded image - just placed on the model. ${lighting}, ${background}, ${styleDesc}, captured from ${selectedAngleDesc}. Professional fashion photography with the jewelry as the main focal point.`;
  };

  // Handle jewelry category change
  const handleCategoryChange = (category) => {
    setJewelryCategory(category);
    // Auto-select "Aesthetic Background" style when "Jewelry Set" is selected (only if not already set)
    if (category === 'Jewelry Set') {
      // Only set to aesthetic if current style is not already set or is default
      if (style === 'realistic' || !style) {
        setStyle('aesthetic');
      }
      // Use the appropriate prompt based on current style
      if (style === 'aesthetic') {
        // Use the custom jewelry set prompt with selected aesthetic background
        const selectedBackground = aestheticBackgroundColor === 'random' || !backgroundColorMap[aestheticBackgroundColor] 
          ? randomChoice(aestheticBackgroundOptions) 
          : backgroundColorMap[aestheticBackgroundColor];
        // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
        const jewelrySetPrompt = `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Create ultra-premium studio photograph of ONLY the jewelry pieces from the uploaded image. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. Arrange ONLY these exact pieces with 2-3 inch spacing on ${selectedBackground}. 

Professional 3-point studio lighting: soft key light at 45°, fill light, rim light for edge definition. Shot with macro lens, f/8-f/11, perfect focus throughout. Remove all mannequins/forms. Ultra-high resolution, perfect color grading, no harsh shadows or blown highlights. Luxury catalog style with rich saturated tones, crystal-clear detail showing metalwork and gemstone faceting. Museum-quality presentation suitable for high-end brand marketing.`;
        setPrompt(jewelrySetPrompt);
      } else {
        // Generate prompt with model for non-aesthetic styles
        const modelPrompt = generateJewelrySetModelPrompt(style, angle, modelGender);
        setPrompt(modelPrompt);
      }
    } else if (category && !useCustomPrompt) {
      // Generate new unique prompt each time with current settings
      const generatedPrompt = generatePromptByCategory(category, style, angle, modelGender, aestheticBackgroundColor);
      setPrompt(generatedPrompt);
    }
  };

  // Handle style change
  const handleStyleChange = (newStyle) => {
    setStyle(newStyle);
    // If category is "Jewelry Set", use appropriate prompt based on style
    if (jewelryCategory === 'Jewelry Set') {
      if (newStyle === 'aesthetic') {
        // Use selected aesthetic background for jewelry set (jewelry only, no model)
        const selectedBackground = aestheticBackgroundColor === 'random' || !backgroundColorMap[aestheticBackgroundColor] 
          ? randomChoice(aestheticBackgroundOptions) 
          : backgroundColorMap[aestheticBackgroundColor];
        // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
        const jewelrySetPrompt = `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Create ultra-premium studio photograph of ONLY the jewelry pieces from the uploaded image. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. Arrange ONLY these exact pieces with 2-3 inch spacing on ${selectedBackground}. 

Professional 3-point studio lighting: soft key light at 45°, fill light, rim light for edge definition. Shot with macro lens, f/8-f/11, perfect focus throughout. Remove all mannequins/forms. Ultra-high resolution, perfect color grading, no harsh shadows or blown highlights. Luxury catalog style with rich saturated tones, crystal-clear detail showing metalwork and gemstone faceting. Museum-quality presentation suitable for high-end brand marketing.`;
        setPrompt(jewelrySetPrompt);
      } else {
        // Generate prompt with model for non-aesthetic styles
        const modelPrompt = generateJewelrySetModelPrompt(newStyle, angle, modelGender);
        setPrompt(modelPrompt);
      }
    } else if (jewelryCategory && !useCustomPrompt) {
      const generatedPrompt = generatePromptByCategory(jewelryCategory, newStyle, angle, modelGender, aestheticBackgroundColor);
      setPrompt(generatedPrompt);
    }
  };

  // Handle regenerate prompt button
  const handleRegeneratePrompt = () => {
    if (jewelryCategory === 'Jewelry Set') {
      // For Jewelry Set, use appropriate prompt based on style
      if (style === 'aesthetic') {
        // Use selected aesthetic background for jewelry set (jewelry only, no model)
        const selectedBackground = aestheticBackgroundColor === 'random' || !backgroundColorMap[aestheticBackgroundColor] 
          ? randomChoice(aestheticBackgroundOptions) 
          : backgroundColorMap[aestheticBackgroundColor];
        // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
        const jewelrySetPrompt = `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Create ultra-premium studio photograph of ONLY the jewelry pieces from the uploaded image. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. Arrange ONLY these exact pieces with 2-3 inch spacing on ${selectedBackground}. 

Professional 3-point studio lighting: soft key light at 45°, fill light, rim light for edge definition. Shot with macro lens, f/8-f/11, perfect focus throughout. Remove all mannequins/forms. Ultra-high resolution, perfect color grading, no harsh shadows or blown highlights. Luxury catalog style with rich saturated tones, crystal-clear detail showing metalwork and gemstone faceting. Museum-quality presentation suitable for high-end brand marketing.`;
        setPrompt(jewelrySetPrompt);
        toast.success('Prompt set for jewelry set!');
      } else {
        // Generate prompt with model for non-aesthetic styles
        const modelPrompt = generateJewelrySetModelPrompt(style, angle, modelGender);
        setPrompt(modelPrompt);
        toast.success('New prompt generated with model!');
      }
    } else if (jewelryCategory && !useCustomPrompt) {
      const newPrompt = generatePromptByCategory(jewelryCategory, style, angle, modelGender, aestheticBackgroundColor);
      setPrompt(newPrompt);
      toast.success('New prompt generated!');
    }
  };
  
  // Handle gender change
  const handleGenderChange = (newGender) => {
    setModelGender(newGender);
    if (jewelryCategory === 'Jewelry Set') {
      // For Jewelry Set, use appropriate prompt based on style
      if (style === 'aesthetic') {
        // Use selected aesthetic background for jewelry set (jewelry only, no model)
        const selectedBackground = aestheticBackgroundColor === 'random' || !backgroundColorMap[aestheticBackgroundColor] 
          ? randomChoice(aestheticBackgroundOptions) 
          : backgroundColorMap[aestheticBackgroundColor];
        // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
        const jewelrySetPrompt = `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Create ultra-premium studio photograph of ONLY the jewelry pieces from the uploaded image. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. Arrange ONLY these exact pieces with 2-3 inch spacing on ${selectedBackground}. 

Professional 3-point studio lighting: soft key light at 45°, fill light, rim light for edge definition. Shot with macro lens, f/8-f/11, perfect focus throughout. Remove all mannequins/forms. Ultra-high resolution, perfect color grading, no harsh shadows or blown highlights. Luxury catalog style with rich saturated tones, crystal-clear detail showing metalwork and gemstone faceting. Museum-quality presentation suitable for high-end brand marketing.`;
        setPrompt(jewelrySetPrompt);
      } else {
        // Generate prompt with model for non-aesthetic styles
        const modelPrompt = generateJewelrySetModelPrompt(style, angle, newGender);
        setPrompt(modelPrompt);
      }
    } else if (jewelryCategory && !useCustomPrompt) {
      const updatedPrompt = generatePromptByCategory(jewelryCategory, style, angle, newGender, aestheticBackgroundColor);
      setPrompt(updatedPrompt);
    }
  };

  // Update prompt when angle, style, or gender changes
  useEffect(() => {
    if (jewelryCategory === 'Jewelry Set') {
      // For Jewelry Set, use appropriate prompt based on style
      if (style === 'aesthetic') {
        // Use selected aesthetic background for jewelry set (jewelry only, no model)
        const selectedBackground = aestheticBackgroundColor === 'random' || !backgroundColorMap[aestheticBackgroundColor] 
          ? randomChoice(aestheticBackgroundOptions) 
          : backgroundColorMap[aestheticBackgroundColor];
        // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
        const jewelrySetPrompt = `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Create ultra-premium studio photograph of ONLY the jewelry pieces from the uploaded image. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. Arrange ONLY these exact pieces with 2-3 inch spacing on ${selectedBackground}. 

Professional 3-point studio lighting: soft key light at 45°, fill light, rim light for edge definition. Shot with macro lens, f/8-f/11, perfect focus throughout. Remove all mannequins/forms. Ultra-high resolution, perfect color grading, no harsh shadows or blown highlights. Luxury catalog style with rich saturated tones, crystal-clear detail showing metalwork and gemstone faceting. Museum-quality presentation suitable for high-end brand marketing.`;
        setPrompt(jewelrySetPrompt);
      } else {
        // Generate prompt with model for non-aesthetic styles
        const modelPrompt = generateJewelrySetModelPrompt(style, angle, modelGender);
        setPrompt(modelPrompt);
      }
    } else if (jewelryCategory && !useCustomPrompt) {
      const updatedPrompt = generatePromptByCategory(jewelryCategory, style, angle, modelGender, aestheticBackgroundColor);
      setPrompt(updatedPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [angle, style, jewelryCategory, useCustomPrompt, modelGender, aestheticBackgroundColor]);

  // Handle file drop/selection
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    
    if (rejectedFiles && rejectedFiles.length > 0) {
      console.error('File rejected:', rejectedFiles[0].errors);
      toast.error('File rejected. Please use JPG or PNG under 10MB');
      return;
    }
    
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      
      toast.success('Image uploaded successfully!');
    }
  }, []);

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    noClick: false,
    noKeyboard: false,
    multiple: false
  });
  

  // Handle image generation
  const handleGenerateImage = async () => {
    if (!selectedFile || !prompt.trim()) {
      toast.error('Please upload an image and enter a prompt');
      return;
    }

    setIsGenerating(true);
    setIsLoading(true);
    setGenerationError(null); // Clear previous errors

    try {
      // For Jewelry Set with aesthetic style, regenerate the prompt with a new random background each time
      // For non-aesthetic styles, use the current prompt (which includes model)
      let finalPrompt = prompt;
      if (jewelryCategory === 'Jewelry Set' && style === 'aesthetic') {
        const selectedBackground = randomChoice(aestheticBackgroundOptions);
        // CRITICAL: Use EXACTLY the jewelry pieces shown in the uploaded image - do not add, remove, or modify anything
        finalPrompt = `CRITICAL INSTRUCTIONS - READ CAREFULLY: You must use ONLY the exact jewelry pieces visible in the uploaded image. DO NOT add any jewelry that is not in the original image. DO NOT change the jewelry type, design, color, or material. DO NOT add forehead ornaments, maang tikka, or any other jewelry pieces that are not shown in the uploaded image.

Create ultra-premium studio photograph of ONLY the jewelry pieces from the uploaded image. Each piece must look EXACTLY as it appears in the original image - same design, same type, same color, same material. Arrange ONLY these exact pieces with 2-3 inch spacing on ${selectedBackground}. 

Professional 3-point studio lighting: soft key light at 45°, fill light, rim light for edge definition. Shot with macro lens, f/8-f/11, perfect focus throughout. Remove all mannequins/forms. Ultra-high resolution, perfect color grading, no harsh shadows or blown highlights. Luxury catalog style with rich saturated tones, crystal-clear detail showing metalwork and gemstone faceting. Museum-quality presentation suitable for high-end brand marketing.`;
      }
      // For non-aesthetic styles with Jewelry Set, the prompt already includes model, so use it as is

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('prompt', finalPrompt);
      formData.append('style', style);
      formData.append('aspectRatio', aspectRatio); // Include aspect ratio
      formData.append('angle', angle); // Include angle option
      formData.append('modelGender', modelGender); // Include model gender option
      formData.append('abstractBackground', (style === 'aesthetic') ? 'true' : 'false'); // Include abstract background option (when style is aesthetic)
      // Include jewelry category if selected
      if (jewelryCategory) {
        formData.append('jewelryCategory', jewelryCategory);
      }

      // Make API request to backend
      const response = await axios.post(`${BACKEND_URL}/api/generate-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000 // 60 second timeout
      });

      if (response.data.success) {
        setGeneratedImage(response.data.data.generatedImage);
        
        // Update wallet and stats from response
        if (response.data.data.wallet) {
          setWalletInfo(response.data.data.wallet);
        }
        if (response.data.data.imagesGenerated !== undefined) {
          setUserStats(prev => ({
            ...prev,
            imagesGenerated: response.data.data.imagesGenerated
          }));
        }
        
        toast.success(`Image generated successfully! Cost: 2 credits. Remaining: ${response.data.data.wallet?.credits || 0} credits`);
      } else {
        // Handle insufficient credits error with detailed messages
        if (response.data.error === 'Insufficient credits') {
          const message = response.data.data?.message || `Insufficient credits! Required: ${response.data.data?.required || 2}, Available: ${response.data.data?.available || 0}`;
          toast.error(message);
        } else if (response.data.error === 'Wallet not found. Please contact support.') {
          toast.error('Wallet not found. Please contact support.');
        } else {
          throw new Error(response.data.error || 'Failed to generate image');
        }
      }
    } catch (error) {
      console.error('Error generating image:', error);
      
      // Helper function to convert technical errors to user-friendly messages
      const getUserFriendlyError = (err) => {
        const errMsg = err?.response?.data?.error || err?.message || '';
        const errCode = err?.code || err?.response?.status;
        
        // Server error messages
        if (errMsg.toLowerCase().includes('insufficient credits')) {
          return 'You don\'t have enough credits. Please recharge your wallet to continue.';
        }
        if (errMsg.toLowerCase().includes('image conversion failed') || errMsg.toLowerCase().includes('corrupted') || errMsg.toLowerCase().includes('unsupported format')) {
          return 'The image file couldn\'t be processed. Please try a different image file (JPEG, PNG, GIF, or WebP).';
        }
        if (errMsg.toLowerCase().includes('failed to process image') || errMsg.toLowerCase().includes('invalid format')) {
          return 'We couldn\'t process your image. Please make sure it\'s a valid image file and try again.';
        }
        if (errMsg.toLowerCase().includes('api key not configured') || errMsg.toLowerCase().includes('api access denied')) {
          return 'Service temporarily unavailable. Please try again in a few moments.';
        }
        if (errMsg.toLowerCase().includes('timeout') || errCode === 'ECONNABORTED') {
          return 'This is taking longer than expected. Please try again with a smaller image or different style.';
        }
        if (errMsg.toLowerCase().includes('too many requests') || errCode === 429) {
          return 'Too many requests right now. Please wait a moment and try again.';
        }
        if (errMsg.toLowerCase().includes('network') || errMsg.toLowerCase().includes('connection') || errCode === 'ECONNREFUSED' || errCode === 'ENOTFOUND') {
          return 'Unable to connect. Please check your internet connection and try again.';
        }
        if (errMsg.toLowerCase().includes('service is temporarily unavailable') || errCode === 503 || errCode === 500) {
          return 'Service temporarily unavailable. Please try again in a few moments.';
        }
        if (errMsg.toLowerCase().includes('wallet not found')) {
          return 'Account issue detected. Please contact support for assistance.';
        }
        
        // Default friendly message
        if (errMsg && !errMsg.includes('ECONN') && !errMsg.includes('timeout') && !errMsg.includes('network')) {
          // If it's already a readable message, use it but simplify if needed
          return errMsg.replace(/API error|Failed to|Error:/gi, '').trim() || 'Something went wrong. Please try again.';
        }
        
        return 'Unable to generate your image right now. Please try again in a moment.';
      };
      
      const errorMessage = getUserFriendlyError(error);
      toast.error(errorMessage);
      setGenerationError(errorMessage);
    } finally {
      setIsGenerating(false);
      setIsLoading(false);
    }
  };

  // Reset the form
  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setJewelryCategory('');
    setUseCustomPrompt(false);
    setPrompt('Transform this jewelry into a professional model photoshoot. Show a beautiful Indian model wearing this jewelry piece elegantly. Focus on the jewelry details, make it the main subject. Professional lighting, clean background, high-quality fashion photography style.');
    setStyle('realistic');
    setAspectRatio('1:1');
    setAngle('front');
    setGeneratedImage(null);
    setIsGenerating(false);
    setIsLoading(false);
    setGenerationError(null); // Clear error on reset
  };

  // Download generated image from S3 URL or data URL
  const handleDownload = async () => {
    if (!generatedImage) return;
    
    try {
      let downloadUrl = generatedImage;
      let filename = `generated-image-${Date.now()}.jpg`;
      
      // If it's an S3 URL or regular HTTP URL, fetch it first
      if (generatedImage.startsWith('http://') || generatedImage.startsWith('https://')) {
        const response = await fetch(generatedImage);
        const blob = await response.blob();
        downloadUrl = window.URL.createObjectURL(blob);
      }
      
      // Create download link with proper attributes to prevent tab opening
        const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_self'; // Prevent opening in new tab
      link.style.display = 'none'; // Hide the link
      link.setAttribute('download', filename); // Ensure download attribute is set
      
      // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        if (downloadUrl !== generatedImage && downloadUrl.startsWith('blob:')) {
          window.URL.revokeObjectURL(downloadUrl);
        }
      }, 100);
      
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image. Please try again.');
    }
  };

  // Authentication check
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading getphotoshoot.ai...</p>
      </div>
    );
  }

  // Main App content component
  const AppContent = () => {
    // Meta Pixel Code - Initialize after login
    useEffect(() => {
      if (isAuthenticated() && user) {
        // Execute the exact Meta Pixel code
        /* eslint-disable */
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('init', '853274407274111');
          window.fbq('track', 'CompleteRegistration', {
            value: 1,
            currency: 'INR',
          });
        }
        /* eslint-enable */
      }
    }, [user]);

    if (!isAuthenticated()) {
      return (
        <>
          <LoginPage />
          <FeaturesSection />
          <PricingSection />
          <FAQ />
          <CTABanner />
          <Footer />
        </>
      );
    }

    // Note: Phone number is optional - user can skip
    // Modal will appear but user can skip if they want

    return (
    <div className="App">
      {/* Meta Pixel Code */}
      <noscript>
        <img 
          height="1" 
          width="1" 
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=853274407274111&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
      {/* End Meta Pixel Code */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <AppHeader 
        credits={walletInfo.credits} 
        generated={walletInfo.totalSpent || 0} 
        user={user} 
        onLogout={logout}
        onRecharge={() => setShowRechargeModal(true)}
      />
      
      <RechargeModal 
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        onSuccess={() => {
          setShowRechargeModal(false);
          fetchUserData(); // Refresh wallet info
        }}
      />

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          <div className="content-grid">
            {/* Clean Simple Layout */}
            <div className="simple-layout">
              
              {/* Upload Section - First */}
              <div className="upload-section-wrapper">
                <div className="upload-card minimal-card">
                  <div className="card-header">
                    <h3 className="card-title">Your Jewelry Photo</h3>
                    {previewUrl && (
                      <button 
                        className="clear-btn-small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReset();
                        }}
                        type="button"
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                  <div
                    {...getRootProps({
                      onClick: (e) => {
                        // Don't prevent default - let dropzone handle it
                      }
                    })}
                    className={`upload-area minimal-upload ${isDragActive ? 'active' : ''}`}
                    style={{ position: 'relative', cursor: 'pointer', touchAction: 'manipulation' }}
                    onMouseDown={(e) => {
                      // Try to open file picker immediately on mousedown
                      if (fileInputRef.current) {
                        setTimeout(() => {
                          fileInputRef.current.click();
                        }, 0);
                      }
                    }}
                    onTouchStart={(e) => {
                      // For mobile - directly trigger input
                      e.preventDefault();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      } else if (open) {
                        open();
                      }
                    }}
                  >
                    <input 
                      {...getInputProps({
                        onChange: (e) => {
                        }
                      })} 
                      ref={fileInputRef}
                      id="jewelry-upload-input"
                      aria-label="Upload jewelry photo"
                      style={{ 
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 1000,
                        fontSize: 0,
                        margin: 0,
                        padding: 0,
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        display: 'block'
                      }}
                    />
                    <label 
                      htmlFor="jewelry-upload-input"
                      style={{ 
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        cursor: 'pointer',
                        pointerEvents: 'none',
                        zIndex: 998
                      }}
                    />
                    {previewUrl ? (
                      <div className="preview-wrapper" style={{ pointerEvents: 'none', zIndex: 1 }}>
                        <img src={previewUrl} alt="Your jewelry" className="preview-img" />
                      </div>
                    ) : (
                      <div className="upload-content" style={{ pointerEvents: 'none', position: 'relative', zIndex: 1 }}>
                        <div className="upload-icon">📷</div>
                        <p className="upload-text">Click here or drag your jewelry photo</p>
                        <p className="upload-hint">Supports: JPG, PNG (up to 10MB)</p>
                      </div>
                    )}
                  </div>
                  {!previewUrl && (
                    <p className="instruction-text">
                      <strong>Tip:</strong> Use a clear photo of your jewelry on a plain background for best results
                    </p>
                  )}
                </div>

                {/* Controls Row: Right After Upload on Mobile */}
                <div className="input-row minimal-input-row controls-section mobile-controls">
                <div className="input-group">
                  <label htmlFor="jewelry-category-select-mobile" className="input-label">
                    Jewelry Type
                    <span className="help-text">Select category</span>
                  </label>
                  <select
                    id="jewelry-category-select-mobile"
                    className="category-select"
                    value={jewelryCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    aria-label="Jewelry category selection"
                  >
                    <option value="">-- Select Type --</option>
                    <option value="Necklaces / Necklace Sets">Necklaces</option>
                    <option value="Earrings">Earrings</option>
                    <option value="Rings">Rings</option>
                    <option value="Bracelets">Bracelets</option>
                    <option value="Bangles">Bangles</option>
                    <option value="Pendants">Pendants</option>
                    <option value="Chains">Chains</option>
                    <option value="Anklets / Payal">Anklets</option>
                    <option value="Brooches">Brooches</option>
                    <option value="Nose Rings / Nose Pins">Nose Pins</option>
                    <option value="Mangalsutra">Mangalsutra</option>
                    <option value="Waist Belts / Vaddanam / Kamarbandh">Waist Belts</option>
                    <option value="Maang Tikka / Head Jewelry">Head Jewelry</option>
                    <option value="Hair Accessories">Hair Accessories</option>
                    <option value="Jewelry Set">Jewelry Set</option>
                  </select>
                </div>
                
                <div className="input-group">
                  <label htmlFor="photography-style-select-mobile" className="input-label">
                    Style
                    <span className="help-text">Photography style</span>
                  </label>
                  <select
                    id="photography-style-select-mobile"
                    className="style-select"
                    value={style}
                    onChange={(e) => handleStyleChange(e.target.value)}
                    aria-label="Photography style selection"
                  >
                    <option value="aesthetic">Aesthetic Background</option>
                    <option value="realistic">Professional</option>
                    <option value="traditional">Traditional</option>
                    <option value="vintage">Vintage</option>
                    <option value="modern">Modern</option>
                    <option value="luxury">Luxury</option>
                    <option value="minimalist">Simple & Clean</option>
                    <option value="artistic">Artistic</option>
                  </select>
                </div>

                {/* Background Color Selection - Only show when Aesthetic Background is selected */}
                {style === 'aesthetic' && (
                  <div className="input-group">
                    <label htmlFor="background-color-select-mobile" className="input-label">
                      Background Color
                      <span className="help-text">Select color</span>
                    </label>
                    <select
                      id="background-color-select-mobile"
                      className="style-select"
                      value={aestheticBackgroundColor}
                      onChange={(e) => setAestheticBackgroundColor(e.target.value)}
                      aria-label="Background color selection"
                    >
                      <option value="random">Random</option>
                      <option value="emerald">Emerald</option>
                      <option value="burgundy">Burgundy</option>
                      <option value="navy">Navy</option>
                      <option value="charcoal">Charcoal</option>
                      <option value="white">White</option>
                      <option value="champagne">Champagne</option>
                    </select>
                  </div>
                )}

                <div className="input-group">
                  <label htmlFor="size-aspect-ratio-select-mobile" className="input-label">
                    Size
                    <span className="help-text">Aspect ratio</span>
                  </label>
                  <select
                    id="size-aspect-ratio-select-mobile"
                    className="style-select"
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    aria-label="Aspect ratio selection"
                  >
                    <option value="1:1">Square (1:1)</option>
                    <option value="2:3">Portrait (2:3)</option>
                    <option value="1:3">Vertical (1:3)</option>
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="angle-select-mobile" className="input-label">
                    Angle
                    <span className="help-text">Viewing angle</span>
                  </label>
                  <select
                    id="angle-select-mobile"
                    className="style-select"
                    value={angle}
                    onChange={(e) => setAngle(e.target.value)}
                    aria-label="Angle selection"
                  >
                    <option value="front">Front View</option>
                    <option value="side">Side View</option>
                    <option value="45-degree">45° Angle</option>
                    <option value="three-quarter">Three-Quarter</option>
                    <option value="diagonal">Diagonal</option>
                    <option value="profile">Profile</option>
                    <option value="upward">Upward Angle</option>
                    <option value="downward">Downward Angle</option>
                    <option value="eye-level">Eye Level</option>
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="model-gender-select-mobile" className="input-label">
                    Model
                    <span className="help-text">Model gender</span>
                  </label>
                  <select
                    id="model-gender-select-mobile"
                    className="style-select"
                    value={modelGender}
                    onChange={(e) => handleGenderChange(e.target.value)}
                    aria-label="Model gender selection"
                  >
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                </div>
                
                <div className="button-group">
                        <button
                          className="generate-btn"
                          onClick={handleGenerateImage}
                    disabled={isGenerating || !selectedFile || !jewelryCategory || walletInfo.credits <= 0}
                  >
                    {isGenerating ? (
                      <>
                        <span className="spinner-small"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        ✨ Generate <span className="credit-info">({generationCost} credits)</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

                {/* Generated Section - Second column on desktop, hidden on mobile */}
                <div className="generated-card minimal-card result-section desktop-result">
                  <h3 className="card-title">Model Photo Result</h3>
                  
                  {isLoading && (
                    <div className="loading-area">
                      <div className="loading-spinner-large"></div>
                      <p className="loading-text-main">Creating your model photo...</p>
                      <p className="loading-text-sub">This may take 30-60 seconds</p>
                    </div>
                  )}

                  {generationError && !isLoading && (
                    <div className="error-display-area">
                      <div className="error-icon">⚠️</div>
                      <p className="error-message-text">{generationError}</p>
                      <button 
                        className="error-dismiss-btn"
                        onClick={() => setGenerationError(null)}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {generatedImage && !isLoading && !generationError && (
                    <div className="result-area">
                      <img 
                        src={generatedImage} 
                        alt="Model wearing your jewelry" 
                        className="result-img"
                      />
                      <div className="result-actions-minimal">
                        <button
                          className="download-btn-minimal"
                          onClick={handleDownload}
                        >
                          📥 Download Image
                        </button>
                          <button 
                          className="reset-btn-minimal"
                          onClick={handleReset}
                        >
                          Create Another
                          </button>
                        </div>
                      </div>
                  )}

                  {!generatedImage && !isLoading && !generationError && (
                    <div className="empty-area">
                      <div className="empty-icon-large">👤</div>
                      <p className="empty-text-main">Your result will appear here</p>
                      <p className="empty-text-sub">Upload a jewelry photo above and click Generate</p>
                      </div>
                    )}
                  </div>
                </div>

              {/* Mobile Result - Below controls on mobile only */}
              <div className="generated-card minimal-card result-section mobile-result">
                <h3 className="card-title">Model Photo Result</h3>
                  
                  {isLoading && (
                    <div className="loading-area">
                    <div className="loading-spinner-large"></div>
                    <p className="loading-text-main">Creating your model photo...</p>
                    <p className="loading-text-sub">This may take 30-60 seconds</p>
                    </div>
                  )}

                  {generationError && !isLoading && (
                    <div className="error-display-area">
                      <div className="error-icon">⚠️</div>
                      <p className="error-message-text">{generationError}</p>
                      <button 
                        className="error-dismiss-btn"
                        onClick={() => setGenerationError(null)}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {generatedImage && !isLoading && !generationError && (
                    <div className="result-area">
                      <img 
                        src={generatedImage} 
                      alt="Model wearing your jewelry" 
                        className="result-img"
                      />
                    <div className="result-actions-minimal">
                      <button
                        className="download-btn-minimal"
                        onClick={handleDownload}
                      >
                        📥 Download Image
                      </button>
                      <button
                        className="reset-btn-minimal"
                        onClick={handleReset}
                      >
                        Create Another
                      </button>
                    </div>
                    </div>
                  )}

                  {!generatedImage && !isLoading && !generationError && (
                    <div className="empty-area">
                    <div className="empty-icon-large">👤</div>
                    <p className="empty-text-main">Your result will appear here</p>
                    <p className="empty-text-sub">Upload a jewelry photo above and click Generate</p>
                  </div>
                )}
              </div>

              {/* Desktop Controls: Below Images on Desktop Only */}
              <div className="input-row minimal-input-row controls-section desktop-controls">
                <div className="input-group">
                  <label htmlFor="jewelry-category-select-desktop" className="input-label">
                    Jewelry Type
                    <span className="help-text">Select category</span>
                  </label>
                  <select
                    id="jewelry-category-select-desktop"
                    className="category-select"
                    value={jewelryCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    aria-label="Jewelry category selection"
                  >
                    <option value="">-- Select Type --</option>
                    <option value="Necklaces / Necklace Sets">Necklaces</option>
                    <option value="Earrings">Earrings</option>
                    <option value="Rings">Rings</option>
                    <option value="Bracelets">Bracelets</option>
                    <option value="Bangles">Bangles</option>
                    <option value="Pendants">Pendants</option>
                    <option value="Chains">Chains</option>
                    <option value="Anklets / Payal">Anklets</option>
                    <option value="Brooches">Brooches</option>
                    <option value="Nose Rings / Nose Pins">Nose Pins</option>
                    <option value="Mangalsutra">Mangalsutra</option>
                    <option value="Waist Belts / Vaddanam / Kamarbandh">Waist Belts</option>
                    <option value="Maang Tikka / Head Jewelry">Head Jewelry</option>
                    <option value="Hair Accessories">Hair Accessories</option>
                    <option value="Jewelry Set">Jewelry Set</option>
                  </select>
                </div>
                
                <div className="input-group">
                  <label htmlFor="photography-style-select-desktop" className="input-label">
                    Style
                    <span className="help-text">Photography style</span>
                  </label>
                  <select
                    id="photography-style-select-desktop"
                    className="style-select"
                    value={style}
                    onChange={(e) => handleStyleChange(e.target.value)}
                    aria-label="Photography style selection"
                  >
                    <option value="aesthetic">Aesthetic Background</option>
                    <option value="realistic">Professional</option>
                    <option value="traditional">Traditional</option>
                    <option value="vintage">Vintage</option>
                    <option value="modern">Modern</option>
                    <option value="luxury">Luxury</option>
                    <option value="minimalist">Simple & Clean</option>
                    <option value="artistic">Artistic</option>
                  </select>
                </div>

                {/* Background Color Selection - Only show when Aesthetic Background is selected */}
                {style === 'aesthetic' && (
                  <div className="input-group">
                    <label htmlFor="background-color-select-desktop" className="input-label">
                      Background Color
                      <span className="help-text">Select color</span>
                    </label>
                    <select
                      id="background-color-select-desktop"
                      className="style-select"
                      value={aestheticBackgroundColor}
                      onChange={(e) => setAestheticBackgroundColor(e.target.value)}
                      aria-label="Background color selection"
                    >
                      <option value="random">Random</option>
                      <option value="emerald">Emerald</option>
                      <option value="burgundy">Burgundy</option>
                      <option value="navy">Navy</option>
                      <option value="charcoal">Charcoal</option>
                      <option value="white">White</option>
                      <option value="champagne">Champagne</option>
                    </select>
                  </div>
                )}

                <div className="input-group">
                  <label htmlFor="size-aspect-ratio-select-desktop" className="input-label">
                    Size
                    <span className="help-text">Aspect ratio</span>
                  </label>
                  <select
                    id="size-aspect-ratio-select-desktop"
                    className="style-select"
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    aria-label="Aspect ratio selection"
                  >
                    <option value="1:1">Square (1:1)</option>
                    <option value="2:3">Portrait (2:3)</option>
                    <option value="1:3">Vertical (1:3)</option>
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="angle-select-desktop" className="input-label">
                    Angle
                    <span className="help-text">Viewing angle</span>
                  </label>
                  <select
                    id="angle-select-desktop"
                    className="style-select"
                    value={angle}
                    onChange={(e) => setAngle(e.target.value)}
                    aria-label="Angle selection"
                  >
                    <option value="front">Front View</option>
                    <option value="side">Side View</option>
                    <option value="45-degree">45° Angle</option>
                    <option value="three-quarter">Three-Quarter</option>
                    <option value="diagonal">Diagonal</option>
                    <option value="profile">Profile</option>
                    <option value="upward">Upward Angle</option>
                    <option value="downward">Downward Angle</option>
                    <option value="eye-level">Eye Level</option>
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="model-gender-select-desktop" className="input-label">
                    Model
                    <span className="help-text">Model gender</span>
                  </label>
                  <select
                    id="model-gender-select-desktop"
                    className="style-select"
                    value={modelGender}
                    onChange={(e) => handleGenderChange(e.target.value)}
                    aria-label="Model gender selection"
                  >
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                </div>
                
                <div className="button-group">
                  <button
                    className="generate-btn"
                    onClick={handleGenerateImage}
                    disabled={isGenerating || !selectedFile || !jewelryCategory || walletInfo.credits <= 0}
                  >
                    {isGenerating ? (
                      <>
                        <span className="spinner-small"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        ✨ Generate <span className="credit-info">({generationCost} credits)</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Advanced options (collapsible) */}
              {jewelryCategory && (
                <div className="advanced-section">
                  <button
                    className="advanced-toggle"
                    onClick={() => setUseCustomPrompt(!useCustomPrompt)}
                  >
                    {useCustomPrompt ? '▼' : '▶'} Advanced Options {useCustomPrompt ? '(Hide)' : '(Show)'}
                  </button>
                  
                  {useCustomPrompt && (
                    <div className="advanced-content">
                      <label htmlFor="custom-prompt-textarea" className="input-label">
                        Custom Description (Optional)
                        <span className="help-text">Describe how you want the model to wear it</span>
                      </label>
                      <textarea
                        id="custom-prompt-textarea"
                        className="prompt-input minimal-prompt"
                        placeholder="e.g., A beautiful Indian model wearing this necklace elegantly..."
                        value={prompt}
                        aria-label="Custom prompt description"
                        onChange={(e) => setPrompt(e.target.value)}
                      />
                      <button
                        className="regenerate-btn-small"
                        onClick={handleRegeneratePrompt}
                        type="button"
                      >
                        Generate New Description
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
    );
  };

  const router = createBrowserRouter(
    [
      { path: '/tos', element: <><SimpleHeader /><TermsPage /><PricingSection /><FAQ /><CTABanner /><Footer /></> },
      { path: '/privacy-policy', element: <><SimpleHeader /><PrivacyPolicyPage /><PricingSection /><FAQ /><CTABanner /><Footer /></> },
      { path: '/refunds', element: <><SimpleHeader /><RefundPolicyPage /><PricingSection /><FAQ /><CTABanner /><Footer /></> },
      { path: '/contact', element: <><SimpleHeader /><ContactPage /><PricingSection /><FAQ /><CTABanner /><Footer /></> },
      { path: '/about', element: <><SimpleHeader /><AboutUsPage /><PricingSection /><FAQ /><CTABanner /><Footer /></> },
      { path: '/pricing', element: <><SimpleHeader /><PricingPage /><PricingSection /><FAQ /><CTABanner /><Footer /></> },
      { path: '/payment-status', element: <PaymentStatusPage /> },
      { path: '/test-phonepe', element: <PhonePeTestPage /> },
      { path: '/merge', element: <MergePage /> },
      { path: '/generated-report', element: <GeneratedReportPage /> },
      { path: '/admin', element: <AdminPanel /> },
      { path: '*', element: <AppContent /> },
    ]
  );

  return (
    <RouterProvider 
      router={router}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    />
  );
}

export default App;
